import registry from './tokens.json';
import {
  chroma,
  hexToHsl,
  hslToHex,
  isHex,
  mix,
  readableOn,
  toChannels,
} from './color';

/**
 * The theme engine.
 *
 * A theme is at most six colours (ThemeColors). Every colour used in the app is a
 * token (tokens.json) whose default is the colour it has always been, and each
 * token is recalculated from the theme according to its role:
 *
 *  - primary / secondary: moved to the primary or secondary hue, keeping the
 *    token's shade relative to the original, so light tints stay light tints and
 *    dark shades stay dark.
 *  - neutral: placed between the text and page background colours at the same
 *    lightness it had between the original text and background; grey-blue tints
 *    keep their tint, following the primary hue.
 *  - background: the page background itself.
 *
 * The header and footer get their own background, and the text inside them is
 * recalculated so it stays readable on it. With nothing set, the engine outputs
 * nothing and every token keeps its default: the app looks exactly as before.
 */

export interface ThemeColors {
  primary?: string;
  secondary?: string;
  background?: string;
  text?: string;
  header?: string;
  footer?: string;
}

export const THEME_COLOR_KEYS: Array<keyof ThemeColors> = ['primary', 'secondary', 'background', 'text', 'header', 'footer'];

type Role = 'neutral' | 'background' | 'primary' | 'secondary' | 'fixed';

interface Token {
  group: string;
  key: string;
  var: string;
  hex: string;
  role: Role;
}

const TOKENS = (registry as { tokens: Token[] }).tokens;

/** The colours today's design was built around; shifts are measured from these. */
const REF = {
  text: '#111827',
  background: '#ffffff',
  primary: '#2563eb',
  secondary: '#7c3aed',
  header: '#e7dff4',
};

/** Only valid hex colours count; anything else is treated as not set. */
export function cleanColors(raw: Partial<Record<string, unknown>> | null | undefined): ThemeColors {
  const out: ThemeColors = {};
  for (const key of THEME_COLOR_KEYS) {
    const value = raw?.[key];
    if (isHex(value)) out[key] = value.trim().toLowerCase();
  }
  return out;
}

const hueDelta = (a: number, b: number) => ((((a - b) % 360) + 540) % 360) - 180;
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Move a brand colour from the reference hue to the target colour. */
function shift(original: string, reference: string, target: string): string {
  const d = hexToHsl(original);
  const r = hexToHsl(reference);
  const t = hexToHsl(target);

  // A near-grey target (black, white, grey) has no hue to move to.
  if (t.s < 0.08) {
    return hslToHex({ h: t.h, s: t.s, l: d.l >= 0.85 || d.l <= 0.18 ? d.l : clamp(d.l + (t.l - r.l), 0.1, 0.92) });
  }

  const h = t.h + clamp(hueDelta(d.h, r.h), -20, 20);
  const s = clamp(d.s * (t.s / Math.max(r.s, 0.01)), 0, 1);
  // Tints and very dark shades keep their lightness; mid tones follow the target.
  const l = d.l >= 0.85 || d.l <= 0.18 ? d.l : clamp(d.l + (t.l - r.l), 0.12, 0.9);
  return hslToHex({ h, s, l });
}

/** Place a grey between the theme's text and background colours. */
function neutral(original: string, theme: ThemeColors): string | null {
  if (!theme.text && !theme.background && !theme.primary) return null;

  const d = hexToHsl(original);
  let out = original;

  if (theme.text || theme.background) {
    const text = theme.text ?? REF.text;
    const background = theme.background ?? REF.background;
    const lText = hexToHsl(REF.text).l;
    const lBackground = hexToHsl(REF.background).l;
    const k = (d.l - lText) / (lBackground - lText);
    out = k < 0 ? mix(text, '#000000', (lText - d.l) / lText) : mix(text, background, k);
  }

  // Clearly tinted greys (grey-blues, grey-lilacs) keep their tint, following
  // the primary hue. Near-neutral greys stay grey.
  if (chroma(original) >= 0.1) {
    const o = hexToHsl(out);
    const hue = theme.primary ? hexToHsl(theme.primary).h + clamp(hueDelta(d.h, hexToHsl(REF.primary).h), -20, 20) : d.h;
    out = hslToHex({ h: hue, s: d.s, l: o.l });
  }

  return out.toLowerCase() === original.toLowerCase() ? null : out;
}

/** The secondary colour, or one calculated from the primary when not set. */
function secondaryOf(theme: ThemeColors): string | undefined {
  if (theme.secondary) return theme.secondary;
  if (!theme.primary) return undefined;
  const p = hexToHsl(theme.primary);
  return hslToHex({ h: p.h, s: p.s, l: clamp(p.l - 0.08, 0.1, 0.9) });
}

function tokenValue(token: Token, theme: ThemeColors): string | null {
  switch (token.role) {
    case 'primary':
      return theme.primary ? shift(token.hex, REF.primary, theme.primary) : null;
    case 'secondary': {
      const secondary = secondaryOf(theme);
      return secondary ? shift(token.hex, REF.secondary, secondary) : null;
    }
    case 'background':
      return theme.background ?? null;
    case 'neutral':
      return neutral(token.hex, theme);
    default:
      return null;
  }
}

/** Every token that the theme changes, as var name -> hex. */
export function themeValues(theme: ThemeColors): Record<string, string> {
  const out: Record<string, string> = {};
  for (const token of TOKENS) {
    const value = tokenValue(token, theme);
    if (value) out[token.var] = value;
  }
  return out;
}

const declarations = (values: Record<string, string>) =>
  Object.entries(values)
    .map(([name, hex]) => `${name}:${toChannels(hex)};`)
    .join('');

/** Readable text colours for a surface, strong and muted. */
function textOn(surface: string, preferredDark: string) {
  const strong = readableOn(surface, preferredDark, '#ffffff');
  return { strong, muted: mix(strong, surface, 0.35) };
}

/**
 * The stylesheet that applies a theme: token values on :root, and the header
 * and footer backgrounds with readable text inside them. Empty when nothing is
 * set, so the defaults apply.
 */
export function buildThemeCss(input: ThemeColors): string {
  const theme = cleanColors(input as Record<string, unknown>);
  const rules: string[] = [];

  const root = themeValues(theme);
  const primary = theme.primary;
  if (primary) {
    // Text on primary-coloured buttons and badges.
    root['--theme-on-primary'] = readableOn(primary, '#111111', '#ffffff');
  }
  if (Object.keys(root).length) rules.push(`:root{${declarations(root)}}`);

  const darkText = theme.text ?? REF.text;

  // Header: when no header colour is set but the brand colours are, its tint
  // follows the secondary colour like every other brand tint.
  const secondary = secondaryOf(theme);
  const header = theme.header ?? (secondary ? shift(REF.header, REF.secondary, secondary) : undefined);
  if (header) {
    const background = theme.header ? header : `rgb(${toChannels(header)} / 0.4)`;
    // Inside the header, greys are recalculated against the header colour so
    // navigation text stays readable on it.
    const scoped = themeValues({ ...theme, text: readableOn(header, darkText, '#ffffff'), background: header });
    rules.push(`[data-theme-scope="header"]{background:${background} !important;${declarations(scoped)}}`);
  }

  if (theme.footer) {
    const text = textOn(theme.footer, darkText);
    rules.push(
      `[data-theme-scope="footer"]{background:${theme.footer} !important;` +
        `--theme-footer-text:${toChannels(text.strong)};--theme-footer-muted:${toChannels(text.muted)};}`
    );
  }

  return rules.join('\n');
}

/**
 * The themed value of one of today's colours, for places that need a real colour
 * rather than a class: chart props, SVG attributes, canvas drawing.
 */
export function resolveColor(hex: string, theme: ThemeColors): string {
  const key = hex.replace('#', '').toLowerCase();
  const token =
    TOKENS.find((t) => t.group === 'c' && t.key === key) ?? TOKENS.find((t) => t.hex.toLowerCase() === `#${key}`);
  if (!token) return hex;
  return tokenValue(token, cleanColors(theme as Record<string, unknown>)) ?? hex;
}

/**
 * CSS colour for one of today's colours, for inline styles: follows the theme
 * through its custom property, with the original as the fallback.
 */
export function cssColor(hex: string, alpha?: number): string {
  const key = hex.replace('#', '').toLowerCase();
  const token =
    TOKENS.find((t) => t.group === 'c' && t.key === key) ?? TOKENS.find((t) => t.hex.toLowerCase() === `#${key}`);
  const channels = toChannels(`#${key}`);
  const value = token ? `var(${token.var}, ${channels})` : channels;
  return alpha === undefined ? `rgb(${value})` : `rgb(${value} / ${alpha})`;
}
